import type { ReactNode } from "react"
import { ShimmerProvider } from "@shimmer-from-structure/react"
import { useEmployeeStatusChannel } from "@/hooks/use-employee-status-channel"
import { Softphone } from "@/components/v2/voice/softphone"
import { CommandPalette } from "@/components/v2/search/command-palette"
import { Toaster } from "@/components/ui/sonner"
import { FlashToasts } from "@/components/flash-toasts"

interface DefaultLayoutProps {
  children: ReactNode
}

// App-wide shimmer theme. The library defaults to a
// white-on-dark-rgba sweep that reads wrong on our cream
// surfaces — so we tune it to the same tokens the old
// hand-rolled skeletons used: a muted cream block
// (`#E5DFD3`) with a slightly lighter sweep (`#F4F0E8`),
// and a 6px fallback radius so text-height blocks don't
// shimmer as hard squares. Any `<Shimmer>` in the tree
// inherits these unless it overrides them per-instance.
const SHIMMER_CONFIG = {
  backgroundColor: "#E5DFD3",
  shimmerColor: "#F4F0E8",
  fallbackBorderRadius: 6,
} as const

// Uses `min-h-dvh` (dynamic viewport) instead of
// `min-h-screen` (large viewport) so the layout never
// exceeds the visible viewport height when the mobile
// browser's address bar is on screen. Otherwise the body
// becomes scrollable behind the v2 mobile shells — and
// dragging on a non-scrollable element like the bottom
// nav scrolls the document, which reads to the user as
// "the bottom nav is scrollable" (CON-95). The v2 mobile
// shells (`MobileIndexShell`, `MobileShowShell`, companies
// `page-shell`) all sit at `h-[100dvh]` and manage their
// own internal scroll — this layout matches them.
export default function DefaultLayout({ children }: DefaultLayoutProps) {
  // The per-employee channel must live ABOVE the
  // sidebar / mobile-top-bar split so notification +
  // availability events sync regardless of which
  // viewport branch is mounted (CON-82 lesson — realtime
  // listeners belong at the page level, not behind a
  // viewport branch). Unauthenticated pages still mount
  // the layout but the channel rejects without a session.
  useEmployeeStatusChannel()
  return (
    <ShimmerProvider config={SHIMMER_CONFIG}>
      <main className="min-h-dvh bg-white text-neutral-900">
        {children}
        {/* App-level softphone: mounted above the viewport
            split so inbound calls reach the agent on every
            viewport (CON-82). Dormant unless voice is
            enabled for the employee. */}
        <Softphone />
        {/* App-global command palette (CON-183): mounted once
            here so ⌘/Ctrl-K + the sidebar Search button reach
            it from any page, and it persists across
            navigations. Portals to document.body; dormant
            until opened. */}
        <CommandPalette />
        {/* App-global toast surface + Rails flash bridge.
            Mounted once here so a single portal serves every
            page and persists across navigations. */}
        <Toaster />
        <FlashToasts />
      </main>
    </ShimmerProvider>
  )
}
